import React, { useEffect, useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import { Link } from "react-router-dom/cjs/react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {clearCart} from "../../../store/reducers/cart-slice-reducer"
import SpinnerLoader from "../../../components/common/SpinnerLoader";
import { useDispatch } from 'react-redux';

const SuccessIcon =
    <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M15.4695 0.232963C15.8241 0.561287 15.8454 1.1149 15.5171 1.46949L6.14206 11.5945C5.97228 11.7778 5.73221 11.8799 5.48237 11.8748C5.23253 11.8698 4.99677 11.7582 4.83452 11.5681L0.459523 6.44311C0.145767 6.07557 0.18937 5.52327 0.556912 5.20951C0.924454 4.89575 1.47676 4.93936 1.79051 5.3069L5.52658 9.68343L14.233 0.280522C14.5613 -0.0740672 15.1149 -0.0953599 15.4695 0.232963Z" fill="white" />
    </svg>;

const ErrorIcon =
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M1.25628 1.25628C1.59799 0.914573 2.15201 0.914573 2.49372 1.25628L8 6.76256L13.5063 1.25628C13.848 0.914573 14.402 0.914573 14.7437 1.25628C15.0854 1.59799 15.0854 2.15201 14.7437 2.49372L9.23744 8L14.7437 13.5063C15.0854 13.848 15.0854 14.402 14.7437 14.7437C14.402 15.0854 13.848 15.0854 13.5063 14.7437L8 9.23744L2.49372 14.7437C2.15201 15.0854 1.59799 15.0854 1.25628 14.7437C0.914573 14.402 0.914573 13.848 1.25628 13.5063L6.76256 8L1.25628 2.49372C0.914573 2.15201 0.914573 1.59799 1.25628 1.25628Z" fill="white" />
    </svg>;

const InfoIcon =
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M10 1.5H4C2.61929 1.5 1.5 2.61929 1.5 4V10C1.5 11.3807 2.61929 12.5 4 12.5H10C11.3807 12.5 12.5 11.3807 12.5 10V4C12.5 2.61929 11.3807 1.5 10 1.5ZM4 0C1.79086 0 0 1.79086 0 4V10C0 12.2091 1.79086 14 4 14H10C12.2091 14 14 12.2091 14 10V4C14 1.79086 12.2091 0 10 0H4Z" fill="white" />
        <path fillRule="evenodd" clipRule="evenodd" d="M5.25 7C5.25 6.58579 5.58579 6.25 6 6.25H7.25C7.66421 6.25 8 6.58579 8 7V10.5C8 10.9142 7.66421 11.25 7.25 11.25C6.83579 11.25 6.5 10.9142 6.5 10.5V7.75H6C5.58579 7.75 5.25 7.41421 5.25 7Z" fill="white" />
        <path d="M5.75 4C5.75 3.31075 6.31075 2.75 7 2.75C7.68925 2.75 8.25 3.31075 8.25 4C8.25 4.68925 7.68925 5.25 7 5.25C6.31075 5.25 5.75 4.68925 5.75 4Z" fill="white" />
    </svg>;

const STATUS_CONTENT_MAP = {
    succeeded: {
        text: "Payment succeeded",
        iconColor: "#30B130",
        icon: SuccessIcon,
    },
    processing: {
        text: "Your payment is processing.",
        iconColor: "#6D6E78",
        icon: InfoIcon,
    },
    requires_payment_method: {
        text: "Your payment was not successful, please try again.",
        iconColor: "#DF1B41",
        icon: ErrorIcon,
    },
    default: {
        text: "Please wait...",
        iconColor: "#DF1B41",
        icon: ErrorIcon,
    }
};

export default function CompletePage() {
    const stripe = useStripe();
    const authInfo = JSON.parse(localStorage.getItem("authInfo"));
    const [status, setStatus] = useState("default");
    const [intentId, setIntentId] = useState(null);
    const [paymentId, setPaymentId] = useState(null);
    const [invoiceData, setInvoiceData] = useState(null);
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        if (!stripe) return;

        const clientSecret = new URLSearchParams(window.location.search).get(
            "payment_intent_client_secret"
        );

        if (!clientSecret) return;

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
            if (!paymentIntent) return;

            setStatus(paymentIntent.status);
            setIntentId(paymentIntent.id);
            handleInvoiceCreation(paymentIntent.id);
        });
    }, [stripe]);

    const handleInvoiceCreation = async (intentId) => {
        setLoading(true);
        try {
            console.log("=== Starting invoice creation process ===");
            console.log("Intent ID:", intentId);
            
            const invoiceData = await createInvoice(intentId);
            console.log("✅ Invoice created successfully:", invoiceData);
            
            if (invoiceData) {
                console.log("=== Formatting payment data ===");
                const paymentData = formatPaymentData(invoiceData, intentId);
                console.log("✅ Payment data formatted:", paymentData);
                
                console.log("=== Saving payment data ===");
                const paymentId = await managePaymentData(paymentData);
                console.log("Payment ID result:", paymentId);
                
                if (paymentId) {
                    console.log("✅ Payment ID created:", paymentId);
                    setPaymentId(paymentId);
                    
                    const userData = await getProfileById(authInfo.id);
                    console.log("✅ User data retrieved:", userData);
                    
                    if (userData) {
                        console.log("=== Managing order status ===");
                        await manageOrderStatus(invoiceData, paymentId, userData);
                        console.log("✅ Order status managed");
                    }
                } else {
                    console.error("❌ Payment ID is null/undefined");
                    toast.error("Failed to create payment record");
                }
            } else {
                console.error("❌ Invoice data is null/undefined");
                toast.error("Failed to create invoice");
            }
        } catch (error) {
            console.error("❌ Error during invoice creation process:", error);
            toast.error("Payment processing failed: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const createInvoice = async (intentId) => {
        try {
            const reqBody = { paymentIntentId: intentId };
            console.log("Creating invoice with payload:", reqBody);
            
            const response = await axios.post("user/createInvoice/", reqBody, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    Authorization: `Bearer ${authInfo.token}`,
                },
            });

            console.log("Invoice API response:", response.data);

            if (response.data.status) {
                const data = response.data.data;
                setInvoiceData(data);
                return data;
            } else {
                console.error("Invoice creation failed:", response.data.message);
                throw new Error(response.data.message || "Failed to create invoice.");
            }
        } catch (error) {
            console.error("Error in createInvoice:", error);
            if (error.response) {
                console.error("Response data:", error.response.data);
                console.error("Response status:", error.response.status);
            }
            toast.error("Failed to create invoice: " + (error.response?.data?.message || error.message));
            return null;
        }
    };

    /**
     * Enhanced formatPaymentData function with multiple cart data sources
     */
    const formatPaymentData = (invoiceData, paymentIntentId = null) => {
        console.log("=== FORMATPAYMENTDATA DEBUG ===");
        console.log("Invoice data:", invoiceData);
        
        // Validate invoice data structure
        if (!invoiceData || !invoiceData.invoiceItem || !invoiceData.finalizedInvoice) {
            console.error("❌ Invalid invoice data structure:", invoiceData);
            throw new Error("Invalid invoice data structure");
        }
        
        // Extract paymentIntentId
        let extractedPaymentIntentId = "";
        if (paymentIntentId) {
            extractedPaymentIntentId = paymentIntentId;
        } else if (invoiceData.finalizedInvoice.payment_intent) {
            extractedPaymentIntentId = invoiceData.finalizedInvoice.payment_intent;
        }
        
        console.log("Extracted paymentIntentId:", extractedPaymentIntentId);

        // *** ENHANCED CART DATA EXTRACTION WITH MULTIPLE SOURCES ***
        let sellerId = null;
        let cart = [];

        try {
            console.log("=== CART DATA EXTRACTION PROCESS ===");
            
            // SOURCE 1: Invoice metadata cart (PRIMARY)
            if (invoiceData.invoiceItem?.metadata?.cart) {
                try {
                    cart = JSON.parse(invoiceData.invoiceItem.metadata.cart);
                    console.log("✅ Cart from invoice metadata:", cart);
                } catch (parseError) {
                    console.error("❌ Error parsing cart from invoice metadata:", parseError);
                    cart = [];
                }
            }
            
            // SOURCE 2: Backup from localStorage if invoice cart is empty/invalid
            if (!cart || cart.length === 0) {
                try {
                    const backupCart = localStorage.getItem('checkoutCartData');
                    if (backupCart) {
                        cart = JSON.parse(backupCart);
                        console.log("⚠️ Using backup cart from localStorage:", cart);
                    }
                } catch (storageError) {
                    console.warn("❌ Could not retrieve backup cart from localStorage:", storageError);
                }
            }
            
            // SELLDERID EXTRACTION (with multiple fallbacks)
            console.log("=== SELLERID EXTRACTION PROCESS ===");
            
            // STEP 1: Direct from invoice metadata (PRIORITY 1)
            if (invoiceData.invoiceItem?.metadata?.sellerId) {
                sellerId = invoiceData.invoiceItem.metadata.sellerId;
                console.log("✅ Found sellerId in invoice metadata (Priority 1):", sellerId);
            }
            
            // STEP 2: From cart items (PRIORITY 2)
            if (!sellerId && Array.isArray(cart) && cart.length > 0) {
                for (const item of cart) {
                    console.log("Checking cart item for sellerId:", {
                        id: item.id,
                        name: item.name,
                        createdBy: item.createdBy,
                        sellerId: item.sellerId,
                        seller_id: item.seller_id,
                    });

                    // Priority: createdBy -> sellerId -> seller_id
                    if (item.createdBy) {
                        sellerId = item.createdBy;
                        console.log("✅ Found sellerId from cart item createdBy:", sellerId);
                        break;
                    } else if (item.sellerId) {
                        sellerId = item.sellerId;
                        console.log("✅ Found sellerId from cart item sellerId:", sellerId);
                        break;
                    } else if (item.seller_id) {
                        sellerId = item.seller_id;
                        console.log("✅ Found sellerId from cart item seller_id:", sellerId);
                        break;
                    }
                }
            }
            
            // STEP 3: Other metadata locations (ADDITIONAL FALLBACKS)
            if (!sellerId) {
                console.log("Checking additional sellerId locations...");
                
                if (invoiceData.sellerId) {
                    sellerId = invoiceData.sellerId;
                    console.log("✅ Found sellerId in invoiceData root:", sellerId);
                } else if (invoiceData.finalizedInvoice?.metadata?.sellerId) {
                    sellerId = invoiceData.finalizedInvoice.metadata.sellerId;
                    console.log("✅ Found sellerId in finalizedInvoice metadata:", sellerId);
                }
            }
            
            // STEP 4: Emergency fallback from localStorage
            if (!sellerId) {
                try {
                    const storedSellerId = localStorage.getItem('lastKnownSellerId');
                    if (storedSellerId) {
                        sellerId = storedSellerId;
                        console.log("⚠️ Using emergency fallback sellerId from localStorage:", sellerId);
                    }
                } catch (storageError) {
                    console.warn("Could not access localStorage for sellerId fallback");
                }
            }
            
        } catch (error) {
            console.error("❌ Error in cart/sellerId extraction process:", error);
            sellerId = null;
            cart = [];
        }

        console.log("=== CART/SELLERID EXTRACTION RESULT ===");
        console.log("Final sellerId:", sellerId);
        console.log("Final cart:", cart);
        console.log("Cart length:", cart.length);
        console.log("Extraction successful:", !!sellerId && cart.length > 0);
        console.log("=======================================");

        // *** ENHANCED CART DATA FOR BACKEND ***
        const enhancedCart = cart.map(item => ({
            // Ensure all necessary fields are present
            id: item.id || item.productId,
            productId: item.id || item.productId,
            name: item.name || item.title || '',
            price: item.price || 0,
            quantity: item.quantity || 1,
            sellerId: item.sellerId || item.createdBy || item.seller_id || sellerId,
            
            // Optional fields that might be needed
            color: item.color || '',
            size: item.size || '',
            image: item.image || item.featuredImage || '',
            categoryId: item.categoryId || item.category || null,
            
            // Milestone-specific fields if applicable
            isMilestone: item.isMilestone || false,
            milestoneNumber: item.milestoneNumber || null,
            milestoneId: item.milestoneId || null,
            milestoneCode: item.milestoneCode || null,
        }));

        console.log("Enhanced cart for backend:", enhancedCart);
        
        const formattedData = [
            {
                userId: authInfo.id,
                sellerId: sellerId,
                amountPaid: invoiceData.invoiceItem.amount / 100,
                paymentAccount: "Stripe",
                invoiceUrl: invoiceData.finalizedInvoice.invoice_pdf || "",
                paymentStatus: invoiceData.finalizedInvoice.status,
                paymentIntentId: extractedPaymentIntentId,
                cart: enhancedCart, // Send enhanced cart data
                
                // Additional metadata that might be useful for backend processing
                orderType: invoiceData.invoiceItem.metadata.orderType || 'regular',
                isMilestoneOrder: invoiceData.invoiceItem.metadata.isMilestoneOrder === 'true',
                totalItems: enhancedCart.length,
                totalAmount: invoiceData.invoiceItem.amount / 100,
            },
        ];
        
        console.log("✅ Final formatted payment data with enhanced cart:", formattedData);
        return formattedData;
    };

    const managePaymentData = async (paymentData) => {
        try {
            console.log("=== SAVING PAYMENT DATA TO BACKEND ===");
            console.log("Payment data being sent to user/savepayment API:", JSON.stringify(paymentData, null, 2));
            console.log("Auth token exists:", !!authInfo.token);
            
            // Log specific cart data being sent
            if (paymentData[0] && paymentData[0].cart) {
                console.log("Cart data in payment payload:");
                paymentData[0].cart.forEach((item, index) => {
                    console.log(`  Item ${index + 1}:`, {
                        id: item.id,
                        name: item.name,
                        sellerId: item.sellerId,
                        price: item.price,
                        quantity: item.quantity
                    });
                });
            }
            
            const response = await axios.post("user/savepayment", paymentData, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    Authorization: `Bearer ${authInfo.token}`,
                },
            });

            console.log("Payment API full response:", response);
            console.log("Response data:", response.data);
            console.log("Response status field:", response.data.status);
            console.log("Response data field:", response.data.data);

            if (response.data && response.data.status === true) {
                console.log("✅ Payment saved successfully to backend");
                return response.data.data;
            } else {
                console.error("❌ Payment API returned status: false");
                console.error("Error message:", response.data?.message || "Unknown error");
                throw new Error(response.data?.message || "Failed to save payment data - status false");
            }
        } catch (error) {
            console.error("❌ Error in managePaymentData:");
            console.error("Error type:", error.constructor.name);
            console.error("Error message:", error.message);
            
            if (error.response) {
                console.error("Response status:", error.response.status);
                console.error("Response data:", error.response.data);
                toast.error(`Failed to save payment: ${error.response.data?.message || error.response.status}`);
            } else if (error.request) {
                console.error("No response received:", error.request);
                toast.error("No response from server");
            } else {
                console.error("Request setup error:", error.message);
                toast.error("Failed to save payment data: " + error.message);
            }
            
            return null;
        }
    };

    const getProfileById = async (userId) => {
        try {
            console.log("Fetching profile for userId:", userId);
            
            const response = await axios.get(`user/my-profile/${userId}`, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    Authorization: `Bearer ${authInfo.token}`,
                },
            });

            console.log("Profile API response:", response.data);
            
            if (response && response.data && response.data.data) {
                const userData = response.data.data;
                
                // Ensure all required fields exist with fallbacks
                const processedUserData = {
                    ...userData,
                    name: userData.name || "Unknown User",
                    email: userData.email || "",
                    phone: userData.phone || "",
                    address: {
                        street: userData.address?.street || "",
                        city: userData.address?.city || "",
                        country: userData.address?.country || "",
                        zip: userData.address?.zip || ""
                    }
                };
                
                console.log("Processed user data:", processedUserData);
                return processedUserData;
            } else {
                throw new Error("Invalid profile response");
            }
        } catch (error) {
            console.error("Error fetching profile data:", error);
            if (error.response) {
                console.error("Profile API error response:", error.response.data);
            }
            throw error;
        }
    };

    const manageOrderStatus = async (data, paymentId, userData) => {
        try {
            console.log("=== Managing Order Status ===");
            console.log("userData", userData);
            console.log("paymentId", paymentId);
            
            // Check if metadata and cart exist
            if (!data.invoiceItem?.metadata?.cart) {
                // Try to get cart from localStorage backup
                const backupCart = localStorage.getItem('checkoutCartData');
                if (backupCart) {
                    console.log("⚠️ Using backup cart data for order status");
                    try {
                        const parsedCart = JSON.parse(backupCart);
                        data.invoiceItem.metadata = data.invoiceItem.metadata || {};
                        data.invoiceItem.metadata.cart = JSON.stringify(parsedCart);
                    } catch (error) {
                        console.error("❌ Error parsing backup cart data:", error);
                        toast.error("No cart data found for order");
                        return;
                    }
                } else {
                    console.error("❌ No cart data found in invoice metadata or localStorage");
                    toast.error("No product data found for order");
                    return;
                }
            }

            const productData = JSON.parse(data.invoiceItem.metadata.cart);
            console.log("productData for order status:", productData);
            
            if (!Array.isArray(productData) || productData.length === 0) {
                console.error("❌ Invalid or empty product data");
                toast.error("Invalid product data");
                return;
            }

            const orderResId = [];
            
            // Process each product for order status creation
            for (const product of productData) {
                if (!product.name && !product.title) {
                    console.error("❌ Product missing required fields:", product);
                    toast.error("Product name is required.");
                    continue;
                }

                console.log("=== PROCESSING PRODUCT FOR ORDER STATUS ===");
                console.log("Product:", JSON.stringify(product, null, 2));

                const payload = {
                    // Product identification
                    id: product.productId || product.id,
                    name: product.name || product.title || "",
                    title: product.name || product.title || "",
                    image: product.image || product.featuredImage || "",
                    featuredImage: product.image || product.featuredImage || "",
                    description: product.description || "",
                    
                    // Order details
                    status: "Order placed",
                    userId: authInfo.id,
                    coins: product.coins || 0,
                    paymentId: paymentId,
                    
                    // Product details
                    product: {
                        productId: product.productId || product.id,
                        quantity: product.quantity || 1,
                        price: product.price || 0,
                        color: product.color || "",
                        size: product.size || "",
                        sellerId: product.sellerId || product.createdBy || product.seller_id,
                        name: product.name || product.title || "",
                        image: product.image || product.featuredImage || "",
                        
                        // Milestone fields
                        milestone: product.milestoneNumber || null,
                        isMilestone: product.isMilestone || false,
                        totalMilestones: product.totalMilestones || 0,
                        currentMilestone: product.milestoneNumber || 0
                    },
                    
                    // Additional fields
                    service: null,
                    serviceCreateCharge: null,
                    subscriptionPlan: null,
                    
                    // Milestone-specific fields
                    milestone: product.milestoneId || product.milestoneCode || null,
                    isMilestone: product.isMilestone || false,
                    milestoneAmount: product.isMilestone ? product.price : null,
                    totalMilestones: product.totalMilestones || 0,
                    currentMilestone: product.milestoneNumber || 0
                };

                console.log("Order status payload:", JSON.stringify(payload, null, 2));
                
                try {
                    const response = await axios.post("user/updateorderstatus", payload, {
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json;charset=UTF-8",
                            Authorization: `Bearer ${authInfo.token}`,
                        },
                    });
                    
                    console.log("Order status response:", response.data);
                    
                    if (response.data?.status && response.data?.data) {
                        // Only update stock for full product purchases, not milestones
                        if (!product.isMilestone) {
                            onUpdateProductStock(response.data.data);
                        } else {
                            console.log("Skipping stock update for milestone purchase");
                        }
                        orderResId.push(response.data.data.id);
                    } else {
                        console.error("❌ Invalid order status response:", response.data);
                        toast.error(`Failed to create order status for ${product.name || product.title}`);
                    }
                } catch (orderError) {
                    console.error("❌ Error creating order status for product:", product.name || product.title, orderError);
                    if (orderError.response) {
                        console.error("Order status API error:", orderError.response.data);
                        const errorMsg = orderError.response.data?.message || orderError.response.data?.error || "Unknown error";
                        toast.error(`Failed to create order status for ${product.name || product.title}: ${errorMsg}`);
                    }
                }
            }
            
            if (orderResId.length > 0) {
                console.log("✅ Created order statuses:", orderResId);
                onSubmitHandler(orderResId, data, paymentId, userData);
            } else {
                console.error("❌ No order IDs created");
                toast.error("Failed to create any order statuses");
            }
        } catch (error) {
            console.error("❌ Error updating order status:", error);
            if (error.response) {
                console.error("Order status API error:", error.response.data);
            }
            toast.error("Failed to create order statuses");
        }
    };

    const onUpdateProductStock = (data) => {
        try {
            if (!data?.product?.productId?.[0] || !data?.product?.quantity?.[0]) {
                console.error("❌ Invalid product data for stock update:", data);
                return;
            }

            const reqBody = {
                productId: data.product.productId[0],
                reduceQty: data.product.quantity[0]
            };
            
            console.log("Updating product stock:", reqBody);
            
            axios.put(`user/productReduceStock`, reqBody, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': `Bearer ${authInfo.token}`
                }
            }).then(response => {
                if (response.data.status) {
                    console.log("✅ Product stock updated:", response.data);
                } else {
                    console.error("❌ Stock update failed:", response.data);
                }
            }).catch(error => {
                console.error("❌ Stock update error:", error);
            });
        } catch (error) {
            console.error("❌ Error in onUpdateProductStock:", error);
        }
    };

    const onSubmitHandler = (orderResId, data, paymentId, userData) => {
        try {
            console.log("=== Final Order Submission ===");
            console.log("orderResId:", orderResId);
            console.log("userData:", userData);
            console.log("paymentId:", paymentId);
            
            if (!userData?.name) {
                console.error("❌ Invalid user data:", userData);
                toast.error("Invalid user data");
                return;
            }

            if (!paymentId) {
                console.error("❌ Payment ID is missing");
                toast.error("Payment ID is required");
                return;
            }

            if (!orderResId || orderResId.length === 0) {
                console.error("❌ No order status IDs provided");
                toast.error("No order items to save");
                return;
            }

            // *** Extract sellerId from cart data ***
            let sellerId = null;
            try {
                if (data.invoiceItem?.metadata?.cart) {
                    const cart = JSON.parse(data.invoiceItem.metadata.cart);
                    console.log("Extracting sellerId for final order from cart:", cart);
                    
                    if (Array.isArray(cart) && cart.length > 0) {
                        for (const item of cart) {
                            // Priority: createdBy -> sellerId -> seller_id
                            if (item.createdBy) {
                                sellerId = item.createdBy;
                                console.log("✅ Found sellerId from createdBy for final order:", sellerId);
                                break;
                            } else if (item.sellerId) {
                                sellerId = item.sellerId;
                                console.log("✅ Found sellerId from sellerId field for final order:", sellerId);
                                break;
                            } else if (item.seller_id) {
                                sellerId = item.seller_id;
                                console.log("✅ Found sellerId from seller_id field for final order:", sellerId);
                                break;
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("❌ Error extracting sellerId for final order:", error);
            }

            console.log("Final sellerId for order submission:", sellerId);

            const fullname = userData.name;
            const [first, last] = fullname.split(" ");
            const url = 'user/saveorder';
            
            if (data.finalizedInvoice.status === "paid") {
                const reqBody = {
                    userId: authInfo.id,
                    paymentId: paymentId,
                    sellerId: sellerId,
                    billingFirstName: first || userData.name || "",
                    billingLastName: last || "",
                    billingCity: userData.address?.city || "",
                    billingCompanyName: "",
                    billingCounty: userData.address?.country || "",
                    billingPhone: userData.phone || "",
                    billingPostCode: userData.address?.zip || "",
                    billingStreetAddress: userData.address?.street || "",
                    billingStreetAddress1: userData.address?.street || "",
                    billingEmail: userData.email || "",
                    deliveryCharge: parseFloat(data.invoiceItem.metadata.deliveryCharge) || 0,
                    taxPercent: 0,
                    taxAmount: parseFloat(data.invoiceItem.metadata.taxAmount) || 0,
                    discount: parseFloat(data.invoiceItem.metadata.discount) || 0,
                    price: data.invoiceItem.amount / 100,
                    total: data.invoiceItem.amount / 100,
                    orderStatus: orderResId,
                    isActive: true,
                    isService: false,
                    isSubscription: false,
                    orderType: data.invoiceItem.metadata.orderType || "regular",
                    isMilestone: data.invoiceItem.metadata.isMilestoneOrder === "true"
                };

                console.log("✅ Final order payload with sellerId:", reqBody);

                // Prevent duplicate submissions
                const submissionKey = `order_${paymentId}_${authInfo.id}`;
                
                if (window.orderSubmissionInProgress || window[submissionKey]) {
                    console.log("❌ Order submission already in progress, skipping");
                    return;
                }

                window.orderSubmissionInProgress = true;
                window[submissionKey] = true;

                axios.post(url, reqBody, {
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json;charset=UTF-8',
                        'Authorization': `Bearer ${authInfo.token}`,
                    }
                }).then(async (response) => {
                    console.log("✅ Order placed successfully:", response.data);
                    
                    if (response.data.status === true) {
                        toast.success('Order Placed Successfully', { autoClose: 3000 });
                        localStorage.setItem('recentOrderPlaced', 'true');
                        dispatch(clearCart());
                        await clearCartData();
                        // Clean up localStorage
                        try {
                            localStorage.removeItem('checkoutCartData');
                            localStorage.removeItem('pendingMilestoneData');
                            console.log("✅ Cleaned up localStorage data");
                        } catch (cleanupError) {
                            console.warn("⚠️ Error cleaning up localStorage:", cleanupError);
                        }
                        
                    } else {
                        console.error("❌ Order creation failed:", response.data.message);
                        toast.error(response.data.message || 'Failed to create order');
                    }
                }).catch(error => {
                    console.error("❌ Order submission error:", error);
                    
                    let errorMessage = 'Failed to submit order';
                    if (error.response) {
                        console.error("Order API error response:", error.response.data);
                        errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                    }
                    
                    toast.error(errorMessage);
                }).finally(() => {
                    window.orderSubmissionInProgress = false;
                    delete window[submissionKey];
                    
                    setTimeout(() => {
                        console.log("✅ Order submission cooldown completed");
                    }, 2000);
                });
            } else {
                console.error("❌ Invoice not paid:", data.finalizedInvoice.status);
                toast.error('Payment not completed. Please try again.');
            }
        } catch (error) {
            console.error("❌ Error in onSubmitHandler:", error);
            toast.error("Failed to submit order: " + error.message);
        }
    };

    const clearCartData = async () => {
        try {
            const response = await axios.delete(`user/clearCartData/${authInfo.id}`, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': `Bearer ${authInfo.token}`,
                }
            });

            if (response.data.status) {
                console.log('✅ Cart cleared from database:', response.data.message);
            } else {
                console.error('❌ Failed to clear cart from database:', response.data.message);
            }
        } catch (error) {
            console.error('❌ Error clearing cart from database:', error);
        }
    };

    return (
        <div id="payment-status">
            {loading ? (
                <SpinnerLoader />
            ) : (
                <>
                    <div className="container d-flex justify-content-center align-items-center min-vh-100">
                        <div className="card text-center p-5 shadow-lg">
                            <div
                                id="status-icon"
                                className="mx-auto rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                    backgroundColor: STATUS_CONTENT_MAP[status].iconColor,
                                    width: "60px",
                                    height: "60px",
                                }}
                            >
                                {STATUS_CONTENT_MAP[status].icon}
                            </div>
                            <h2 id="status-text">{STATUS_CONTENT_MAP[status].text}</h2>
                            <div className="ctn_btn mt-2">
                                <Link to="/product-listing" className="view_more">
                                    Continue shopping
                                </Link>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}